import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import Dashboard from './components/Dashboard';
import AccountDetail from './components/AccountDetail';
import AdminPanel from './components/AdminPanel';
import HeaderNameControl from './components/HeaderNameControl';
import TodoList from './components/TodoList';
import KoneChat from './components/KoneChat';
import SapAssistant from './components/SapAssistant';
import { isAuthConfigured, loginRequest } from './authConfig';
import './App-enhanced.css';

// ---------------------------------------------------------------------------
// Runtime environment detection (dev vs prod)
// ---------------------------------------------------------------------------
// The frontend is built once and served from different hosts per environment
// (e.g. dev-icop.kone.com for dev, icop.kone.com for prod). We treat the app
// as PRODUCTION only when the hostname carries no non-prod marker; local
// development, dev-* and test/staging hosts are all considered non-prod.
// An explicit REACT_APP_ENV ("dev"/"prod") overrides the hostname heuristic.
const isProdEnvironment = () => {
  const explicit = (process.env.REACT_APP_ENV || '').toLowerCase();
  if (explicit === 'prod' || explicit === 'production') return true;
  if (explicit === 'dev' || explicit === 'development' || explicit === 'test') return false;

  const host = (typeof window !== 'undefined' && window.location && window.location.hostname) || '';
  const h = host.toLowerCase();
  const nonProdMarkers = ['localhost', '127.0.0.1', 'dev-', 'dev.', '-dev', 'test', 'staging', 'stage', 'qa', 'acc'];
  const looksNonProd = nonProdMarkers.some((m) => h.includes(m));
  return !looksNonProd;
};

// SAP is still under test and must only appear in non-prod environments.
const isSapFeatureEnabled = () => !isProdEnvironment();

function AuthConfigError() {
  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="auth-brand-mark">iCOP</div>
        <h2>SSO Configuration Missing</h2>
        <p>
          Set REACT_APP_AZURE_TENANT_ID and REACT_APP_AZURE_CLIENT_ID in frontend environment
          configuration, then rebuild the app.
        </p>
      </div>
    </div>
  );
}

function SignInScreen({ onSignIn }) {
  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="auth-brand-mark">iCOP</div>
        <h2 className="auth-title">KONE iCOP</h2>
        <p className="auth-subtitle">Sign in with your KONE employee account to access the platform.</p>
        <button className="auth-action" onClick={onSignIn}>
          <span className="auth-action-icon" aria-hidden="true">
            🔐
          </span>
          <span className="auth-action-text">Sign In with SSO</span>
        </button>
      </div>
    </div>
  );
}

function AccessDeniedScreen({ message, onSignOut, onRetry }) {
  return (
    <div className="auth-screen">
      <div className="auth-card access-denied-card">
        <div className="auth-brand-mark">iCOP</div>
        <h2>Access Denied</h2>
        <p>{message || 'Your account is signed in, but you do not have permission to use iCOP.'}</p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '16px' }}>
          {onRetry && (
            <button className="auth-action" onClick={onRetry} style={{ backgroundColor: '#2563eb' }}>
              🔄 Try Again (Fresh Login)
            </button>
          )}
          <button className="auth-action" onClick={onSignOut} style={{ backgroundColor: '#6b7280' }}>
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

function Sidebar({ isOpen, onClose, allowedTeams, isAdmin, loading, userEmail }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedTeams, setExpandedTeams] = useState({});

  const allTeams = [
    { name: 'iCOP', displayName: 'iCOP', environments: ['dev', 'prod'] },
    { name: 'IES', displayName: 'IES', environments: ['dev', 'acc', 'prod'] },
    { name: 'SMP', displayName: 'SMP', environments: ['dev', 'acc', 'prod'] },
    { name: 'IRMA', displayName: 'IRMA', environments: ['dev', 'qa', 'prod'] },
    { name: 'MyKONE', displayName: 'MyKONE Delivery Ops', environments: ['dev', 'qa', 'acc', 'prod', 'shared'] },
    { name: 'MyKONE-PSR', displayName: 'MyKONE PSR', environments: ['dev', 'acc', 'prod', 'shared'] },
    { name: 'MyKONE-Platform', displayName: 'MyKONE Platform', environments: ['dev', 'acc', 'prod', 'shared'] },
    { name: 'MyKONE-CIAM', displayName: 'MyKONE CIAM', environments: ['dev', 'acc', 'prod', 'shared'] },
    { name: 'KONE-Support', displayName: 'KONE-Support', environments: ['dev', 'acc', 'prod', 'shared', 'sandbox'] },
    { name: 'LogBook', displayName: 'LogBook', environments: ['dev', 'acc', 'prod'] },
    { name: 'DMI', displayName: 'DMI', environments: ['dev'] },
    { name: 'FLCAD', displayName: 'FLCAD', environments: ['dev'] }
  ];

  // SAP-only user: not an admin and the SOLE allowed access is the SAP feature.
  // These users must see nothing but SAP (no Dashboard, KoneChat, Teams, Admin).
  // Disabled in production, where SAP itself is hidden.
  const nonSapTeams = (allowedTeams || []).filter(t => t && t !== 'SAP');
  const isSapOnly = isSapFeatureEnabled()
    && !isAdmin && (allowedTeams || []).includes('SAP') && nonSapTeams.length === 0;

  // Filter teams based on user access - admins see all, others see only their teams
  // While loading, show no teams (will update once loaded). SAP-only users get none.
  const teams = loading || isSapOnly
    ? [] 
    : isAdmin 
      ? allTeams 
      : allTeams.filter(team => allowedTeams?.includes(team.name));

  // Check if an account is currently selected
  const pathMatch = location.pathname.match(/^\/team\/([^/]+)\/([^/]+)$/);
  const selectedTeam = pathMatch ? pathMatch[1] : null;

  // Auto-expand only the selected team when an account is selected
  React.useEffect(() => {
    if (selectedTeam) {
      // When an account is selected, only expand that team
      setExpandedTeams({
        [selectedTeam]: true
      });
    }
  }, [selectedTeam]);

  const toggleTeam = (teamName) => {
    setExpandedTeams(prev => ({
      ...prev,
      [teamName]: !prev[teamName]
    }));
  };

  const handleAccountClick = (teamName, env) => {
    navigate(`/team/${teamName}/${env}`);
    if (window.innerWidth <= 1024) {
      onClose();
    }
  };

  const handleDashboardClick = () => {
    if (window.innerWidth <= 1024) {
      onClose();
    }
  };
  
  return (
    <div className={`sidebar ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-logo">
        {/* Inline CSS wordmark so no external asset can be blocked
            (e.g. by Prisma Access Browser). */}
        <span className="sidebar-logo-mark" aria-label="KONE" role="img">
          <span className="kone-letter">K</span>
          <span className="kone-letter">O</span>
          <span className="kone-letter">N</span>
          <span className="kone-letter">E</span>
        </span>
      </div>
      <nav className="sidebar-nav">
        {/* Dashboard - hidden from SAP-only users (they see only SAP). */}
        {!isSapOnly && (
          <Link 
            to="/" 
            className={`nav-item ${location.pathname === '/' ? 'active' : ''}`}
            onClick={handleDashboardClick}
          >
            <span className="nav-icon">📊</span>
            <span>Dashboard</span>
          </Link>
        )}

        {/* KoneChat - internal AI assistant. Available to all iCOP users EXCEPT
            SAP-only users, who must see nothing but the SAP feature. */}
        {!isSapOnly && (
          <Link
            to="/konechat"
            className={`nav-item ${location.pathname === '/konechat' ? 'active' : ''}`}
            onClick={handleDashboardClick}
          >
            <span className="nav-icon">💬</span>
            <span>KoneChat</span>
          </Link>
        )}

        {/* TODO List - personal task tracker, its own page. Shown right after
            KoneChat so the feature is discoverable. Hidden from SAP-only users. */}
        {!isSapOnly && (
          <Link
            to="/todos"
            className={`nav-item ${location.pathname === '/todos' ? 'active' : ''}`}
            onClick={handleDashboardClick}
          >
            <span className="nav-icon">✅</span>
            <span>TODO List</span>
          </Link>
        )}

        {/* SAP - dedicated SAP feature, visible to SAP users (and admins) only.
            Hidden in production while still under test (shown in dev). */}
        {isSapFeatureEnabled() && (isAdmin || allowedTeams?.includes('SAP')) && (
          <Link
            to="/sap"
            className={`nav-item ${location.pathname === '/sap' ? 'active' : ''}`}
            onClick={handleDashboardClick}
          >
            <span className="nav-icon">🧩</span>
            <span>SAP</span>
          </Link>
        )}

        {/* Admin Panel link - only visible to admins */}
        {isAdmin && (
          <Link 
            to="/admin" 
            className={`nav-item ${location.pathname === '/admin' ? 'active' : ''}`}
            onClick={handleDashboardClick}
          >
            <span className="nav-icon">🔧</span>
            <span>Admin Panel</span>
          </Link>
        )}
        
        {!isSapOnly && <div className="nav-section-title">Teams</div>}
        
        {isSapOnly ? null : loading ? (
          <div style={{ padding: '8px 16px', color: '#6B7280', fontSize: '13px' }}>
            Loading teams...
          </div>
        ) : teams.length === 0 ? (
          <div style={{ padding: '8px 16px', color: '#6B7280', fontSize: '13px' }}>
            No teams available
          </div>
        ) : (
          teams.map(team => (
            <div key={team.name} className="team-section">
              <div 
                className={`nav-item team-item ${expandedTeams[team.name] ? 'expanded' : ''} ${selectedTeam === team.name ? 'selected-team' : ''}`}
                onClick={() => toggleTeam(team.name)}
              >
                <span className="nav-icon">{expandedTeams[team.name] ? '▼' : '▶'}</span>
                <span>{team.displayName}</span>
              </div>
              {expandedTeams[team.name] && (
                <div className="team-accounts">
                  {team.environments.map(env => (
                    <div
                      key={env}
                      className={`account-item ${location.pathname === `/team/${team.name}/${env}` ? 'active' : ''}`}
                      onClick={() => handleAccountClick(team.name, env)}
                    >
                      <span className={`env-badge env-${env}`}>{env.toUpperCase()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </nav>
    </div>
  );
}

function App() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [accessDeniedMessage, setAccessDeniedMessage] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userAccess, setUserAccess] = useState({ allowedTeams: [], isAdmin: false, loading: true });
  const [darkMode, setDarkMode] = useState(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) {
      return saved === 'true';
    }
    // Check system preference
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Apply dark mode to document
  React.useEffect(() => {
    if (darkMode) {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  useEffect(() => {
    if (accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [accounts, instance]);

  useEffect(() => {
    const onAccessDenied = (event) => {
      const message = event?.detail?.message || 'You do not have access to this application.';
      setAccessDeniedMessage(message);
    };

    const onAuthExpired = () => {
      setAccessDeniedMessage('');
    };

    window.addEventListener('icop-auth-denied', onAccessDenied);
    window.addEventListener('icop-auth-expired', onAuthExpired);
    return () => {
      window.removeEventListener('icop-auth-denied', onAccessDenied);
      window.removeEventListener('icop-auth-expired', onAuthExpired);
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      setAccessDeniedMessage('');
      // Fetch user access info from the teams endpoint (which already returns userAccess)
      // This avoids needing a separate /api/user/access call
      const fetchUserAccess = async () => {
        try {
          const activeAccount = instance.getActiveAccount();
          if (!activeAccount) {
            console.error('No active account available, retrying...');
            setTimeout(() => {
              const retryAccount = instance.getActiveAccount();
              if (retryAccount) {
                fetchUserAccess();
              } else {
                console.error('Still no active account after retry');
                setUserAccess(prev => ({ ...prev, loading: false }));
              }
            }, 500);
            return;
          }
          
          // Use axios which has the auth interceptor configured
          // Fetch from teams endpoint which returns userAccess info
          const axios = (await import('axios')).default;
          const response = await axios.get(`${process.env.REACT_APP_API_URL || ''}/api/teams`);
          
          const data = response.data;
          console.log('Teams response with userAccess:', data.userAccess);
          
          // Teams endpoint returns { teams: [...], userAccess: { isAdmin, allowedTeams } }
          const userAccessData = data.userAccess || {};
          const allowedTeams = userAccessData.allowedTeams || [];
          const isAdmin = userAccessData.isAdmin || false;
          
          console.log('User access parsed - allowedTeams:', allowedTeams, 'isAdmin:', isAdmin);
          
          setUserAccess({
            allowedTeams: allowedTeams,
            isAdmin: isAdmin,
            loading: false
          });
          
          if (allowedTeams.length === 0 && !isAdmin) {
            console.warn('No teams available for user. Check Azure AD groups claim configuration.');
          }
        } catch (error) {
          console.error('Error fetching user access:', error);
          if (error.response) {
            console.error('Response status:', error.response.status);
            console.error('Response data:', error.response.data);
          }
          setUserAccess(prev => ({ ...prev, loading: false }));
        }
      };
      
      fetchUserAccess();
    }
  }, [isAuthenticated, instance, accounts]);

  if (!isAuthConfigured) {
    return <AuthConfigError />;
  }

  if (!isAuthenticated) {
    return <SignInScreen onSignIn={() => instance.loginRedirect(loginRequest)} />;
  }

  const activeAccount = instance.getActiveAccount() || accounts[0];

  // SAP-only user: not an admin and the SOLE allowed access is the SAP feature.
  // These users are routed to /sap and cannot reach any other page.
  // In production SAP is disabled, so the SAP-only fast-path is also disabled
  // (otherwise such a user would be redirected to a hidden page).
  const sapOnlyNonSapTeams = (userAccess.allowedTeams || []).filter(t => t && t !== 'SAP');
  const isSapOnly = isSapFeatureEnabled()
    && !userAccess.loading && !userAccess.isAdmin
    && (userAccess.allowedTeams || []).includes('SAP')
    && sapOnlyNonSapTeams.length === 0;

  const handleSignOut = () => {
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  };

  const handleRetryLogin = async () => {
    // Clear MSAL cache and force a fresh login
    // This can help when Azure AD sends empty groups intermittently
    try {
      await instance.clearCache();
    } catch (e) {
      console.log('Cache clear error (may be normal):', e);
    }
    setAccessDeniedMessage('');
    instance.loginRedirect(loginRequest);
  };

  if (accessDeniedMessage) {
    return <AccessDeniedScreen message={accessDeniedMessage} onSignOut={handleSignOut} onRetry={handleRetryLogin} />;
  }

  return (
    <Router>
      <div className="App">
        <Sidebar 
          isOpen={sidebarOpen} 
          onClose={closeSidebar} 
          allowedTeams={userAccess.allowedTeams}
          isAdmin={userAccess.isAdmin}
          loading={userAccess.loading}
          userEmail={activeAccount?.username}
        />
        <div 
          className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
          onClick={closeSidebar}
        />
        <div className="main-content">
          <header className="App-header">
            <button className="menu-toggle" onClick={toggleSidebar}>
              ☰
            </button>
            <div className="header-text">
              <h1>iCOP</h1>
              <div className="header-subtitle">Intelligent Cloud Optimization Platform</div>
            </div>
            <div className="header-right">
              {/* Static name display. The TODO List has its own left-panel nav item. */}
              <HeaderNameControl />
              <button 
                className="theme-toggle" 
                onClick={toggleDarkMode}
                title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {darkMode ? '☀️' : '🌙'}
              </button>
              <button className="signout-btn" onClick={handleSignOut}>Sign Out</button>
            </div>
          </header>
          {isSapOnly ? (
            // SAP-only users can reach the SAP feature and nothing else; any
            // other path redirects to /sap.
            <Routes>
              <Route path="/sap" element={<SapAssistant />} />
              <Route path="*" element={<Navigate to="/sap" replace />} />
            </Routes>
          ) : (
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/konechat" element={<KoneChat />} />
              <Route path="/todos" element={<TodoList />} />
              {isSapFeatureEnabled() && (
                <Route path="/sap" element={<SapAssistant />} />
              )}
              <Route path="/team/:teamName/:environment" element={<AccountDetail userAccess={userAccess} />} />
              <Route path="/admin" element={
                <AdminPanel 
                  isAdmin={userAccess.isAdmin} 
                  userEmail={activeAccount?.username} 
                />
              } />
            </Routes>
          )}
        </div>
      </div>
    </Router>
  );
}

export default App;
